/**
 * @file FavouritingCounter.js
 *
 * Provides the counter for the wishlist icon.
 *
 * @author Mike Consemulder <mikeconsemulder@studyportals.com>
 * @copyright © 2018 StudyPortals B.V., all rights reserved.
 * @version 1.0.0
 */
import { IWishlistService, WishlistServiceReadyEvent } from '@studyportals/student-interfaces';
import { ISubscriber } from '@studyportals/event-aggregation-service-interface';
import { FavouriteAdded, FavouriteRemoved } from '@studyportals/wishlist-service-core';

/* eslint-disable
	@typescript-eslint/no-unsafe-assignment,
	@typescript-eslint/prefer-for-of,
	@typescript-eslint/no-unused-expressions,
	@typescript-eslint/no-unsafe-argument,
	@typescript-eslint/no-non-null-assertion,
	 @typescript-eslint/no-unused-expressions
 */

export class FavouriteCounter implements ISubscriber<WishlistServiceReadyEvent> {
	private favouriteIcons: HTMLElement[];
	private favouriteLength: number;
	private counterInitiated: boolean;
	private wishlistService?: IWishlistService;

	constructor() {
		this.favouriteIcons = [];
		this.favouriteLength =
			(window.Loot && window.Loot.DataStorage && window.Loot.DataStorage.retrieve('FavouriteCounter/Count')) || 0;
		this.counterInitiated = false;

		this.getFavouriteIcons();
		this.subscribeToHeaderUpdatedEvent();
		this.addWishlistEventListeners();

		this.revealAnimationWhenFavouriteLengthGreaterThanZero();
	}

	private revealAnimationWhenFavouriteLengthGreaterThanZero(): void {
		if (this.favouriteLength > 0) {
			this.revealAnimation();
		}
	}

	public async notify(event: WishlistServiceReadyEvent): Promise<void> {
		if (event.eventType === WishlistServiceReadyEvent.EventType) {
			this.wishlistService = event.wishlistService;
		}

		await this.animate();
	}

	private subscribeToHeaderUpdatedEvent(): void {
		document.addEventListener('header_login_updated', () => {
			if (this.wishlistServiceReady()) {
				this.getFavouriteIcons();
				void this.animate();
			}
		});
	}

	private getFavouriteIcons(): void {
		const favouriteIconNodes = document.querySelectorAll('.WishlistIcon');
		this.favouriteIcons = Array.from(favouriteIconNodes) as HTMLElement[];
	}

	private wishlistServiceReady(): boolean {
		return !!this.wishlistService;
	}

	private addWishlistEventListeners(): void {
		window.EventAggregationService.subscribeTo(WishlistServiceReadyEvent.EventType, this, true);
		window.EventAggregationService.subscribeTo(FavouriteAdded.EventType, this);
		window.EventAggregationService.subscribeTo(FavouriteRemoved.EventType, this);
	}

	/**
	 * Call the animation if needed, or hide the placeholders if the length is 0
	 */
	private async animate(): Promise<void> {
		const oldCount = this.favouriteLength;

		await this.loadFavouritesCount();

		if (oldCount === this.favouriteLength) {
			return;
		}

		this.favouriteLength > 0 ? this.animateCounters() : this.hideCounterPlaceHolders();
	}

	private async loadFavouritesCount(): Promise<void> {
		const wishlist = await this.wishlistService!.getWishlist();
		this.favouriteLength = wishlist.favourites.length;

		if (!window.Loot || !window.Loot.DataStorage) {
			console.warn('Loot failed to load');
			return;
		}

		window.Loot.DataStorage.store('FavouriteCounter/Count', this.favouriteLength as any, 864000);
	}

	private animateCounters(): void {
		//let the heart beat
		this.activateHeartbeat();
		!this.counterInitiated ? this.revealAnimation() : this.additionalAnimation();
	}

	private hideCounterPlaceHolders(): void {
		for (let i = 0; i < this.favouriteIcons.length; i++) {
			const counterPlaceholder = this.favouriteIcons[i].querySelector('span');
			if (counterPlaceholder) {
				counterPlaceholder.classList.add('Hidden');
			}
		}

		this.counterInitiated = false;
	}

	private activateHeartbeat(): void {
		this.favouriteIcons.forEach((favouriteIcon) => {
			favouriteIcon.classList.add('HeartBeat');

			favouriteIcon.addEventListener('animationend', () => {
				favouriteIcon.classList.remove('HeartBeat');
			});
		});
	}

	private placeCounterPlaceHolderInIcon(favouriteIcon: HTMLElement, favouriteLength: number): void {
		//if not exist, place the placeholder
		const wishlistCounter = favouriteIcon.querySelector('.WishlistCounter p') as HTMLElement;
		if (wishlistCounter === null) {
			//place new counter
			favouriteIcon.insertAdjacentHTML(
				'beforeend',
				`<span class="WishlistCounter Hidden"><p>${favouriteLength}</p></span>`
			);
		} else {
			wishlistCounter.innerText = favouriteLength.toString();
		}
	}

	/**
	 * Animation for the revealing of the number, the first time someone favourites or when loading the page
	 */
	private revealAnimation(): void {
		for (let i = 0; i < this.favouriteIcons.length; i++) {
			//place the right number in the placeholder
			this.placeCounterPlaceHolderInIcon(this.favouriteIcons[i], this.favouriteLength);

			//unhide the counter placeholder
			this.unhideCounterPlaceholder(this.favouriteIcons[i]);

			//toggle the right classes
			this.favouriteIcons[i].classList.remove('NotEmpty');
			this.favouriteIcons[i].classList.add('Empty');
		}

		this.counterInitiated = true;
	}

	/**
	 * Animation for increasing or decreasing the favourite list.
	 */
	private additionalAnimation(): void {
		for (let i = 0; i < this.favouriteIcons.length; i++) {
			this.favouriteIcons[i].classList.remove('Empty');
			this.favouriteIcons[i].classList.add('NotEmpty');

			setTimeout(() => {
				//place the right number in the placeholder
				this.placeCounterPlaceHolderInIcon(this.favouriteIcons[i], this.favouriteLength);
				//unhide the counter placeholder
				this.unhideCounterPlaceholder(this.favouriteIcons[i]);
			}, 300);

			setTimeout(() => {
				this.favouriteIcons[i].classList.remove('NotEmpty');
			}, 600);
		}
	}

	private unhideCounterPlaceholder(favouriteIcon: HTMLElement): void {
		//find the span inside the favourite icon
		const counterPlaceholder = favouriteIcon.querySelector('span');
		//if it exist, hide it.
		if (counterPlaceholder) {
			counterPlaceholder.classList.remove('Hidden');
		}
	}
}

document.addEventListener('DOMContentLoaded', () => {
	new FavouriteCounter();
});
